// Pure Connection Eligibility module.
//
// One home for the product rule that decides which contexts a *new* Rolodex
// Entry may be placed in. Personal connections need only a name; Professional
// connections require the phone trust gate, because a professional record is
// the one that can publish a Discovery Edge and be introduced through.

import { CONTEXT_PERSONAL, CONTEXT_PROFESSIONAL } from "./rolodexContext";

/** Digits only; 10-digit US numbers get the country prefix. Mirrors signup. */
export function normalizePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `1${digits}`;
  return digits;
}

export function isUsablePhone(raw) {
  return normalizePhone(raw).length >= 10;
}

/**
 * A registered recipient has already cleared the trust gate through the phone
 * index, so their entry qualifies whether or not the owner retyped the number.
 */
export function canEnableProfessional(draft) {
  return Boolean(draft?.recipient_uid) || isUsablePhone(draft?.recipient_phone);
}

export function canEnablePersonal() {
  return true;
}

export function canEnableContext(context, draft) {
  if (context === CONTEXT_PROFESSIONAL) return canEnableProfessional(draft);
  if (context === CONTEXT_PERSONAL) return canEnablePersonal(draft);
  return false;
}

/** The message the add form shows when a context is gated. Null when allowed. */
export function contextGateReason(context, draft) {
  if (canEnableContext(context, draft)) return null;
  if (context === CONTEXT_PROFESSIONAL) {
    return "A phone number is required for professional connections — it is how Aline confirms the person and can introduce you later.";
  }
  return "This context is not available.";
}

/**
 * Which contexts a draft may be saved into right now.
 *
 * `existingFacets` are the contexts already stored on the entry. Those always
 * remain available: entries migrated from the legacy `connections` collection
 * are professional and may carry no phone, and gating their updates would make
 * them uneditable. The gate applies to *activating a new context*, never to
 * editing one that already exists.
 */
export function allowedContexts(draft, existingFacets = {}) {
  return [CONTEXT_PROFESSIONAL, CONTEXT_PERSONAL].filter(
    (context) => Boolean(existingFacets?.[context]) || canEnableContext(context, draft)
  );
}

/** True when a requested set of contexts is legal for this draft. */
export function contextsAreAllowed(contexts, draft, existingFacets = {}) {
  const allowed = allowedContexts(draft, existingFacets);
  return (contexts || []).every((context) => allowed.includes(context));
}
